import { useUpsertCommodityTypeMutation } from "@/pages/sy/hooks/queries/useCommodityTypesQuery";
import { ErrorModal } from "@dexteel/mesf-core";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import {
  COMMODITY_TYPE_INITIAL_VALUES,
  CommodityType,
} from "../../../models/CommodityType";

type Props = {
  commodityType: CommodityType | null;
  modal: "new" | "edit" | "delete" | "";
  onHide: ({
    shouldUpdate,
    close,
    entityId,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => void;
};

export const CommodityTypeDetails = ({
  commodityType,
  modal,
  onHide,
}: Props) => {
  const [message, setNotificationMessage] = useState<string>("");
  const [error, setError] = useState("");

  const upsertMutation = useUpsertCommodityTypeMutation();

  const isSubmitting = upsertMutation.isPending;
  const isSystemType = modal !== "new" && Boolean(commodityType?.IsSystemType);
  const isReadOnly = modal === "delete" || isSystemType;

  const {
    reset,
    handleSubmit,
    control,
    formState: { dirtyFields },
  } = useForm<CommodityType>({
    mode: "onChange",
  });

  const isFormDirty = Object.keys(dirtyFields).length > 0 || modal === "new";

  const onSubmit: SubmitHandler<CommodityType> = async (data) => {
    if (modal === "delete" || isSystemType) return;

    try {
      const result = await upsertMutation.mutateAsync({
        ...data,
        CommodityTypeName: data.CommodityTypeName.trimEnd(),
      });

      setNotificationMessage("The commodity type was saved successfully.");
      onHide({
        shouldUpdate: true,
        close: false,
        entityId: result.entityId,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to save commodity type",
      );
    }
  };

  useEffect(() => {
    if (modal !== "") {
      reset(commodityType || COMMODITY_TYPE_INITIAL_VALUES);
      setError("");
    }
  }, [modal, commodityType?.CommodityTypeId]);

  return (
    <>
      {commodityType === null && modal !== "new" ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography>Select a commodity type or create a new one</Typography>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} style={{ padding: "20px" }}>
            <Grid item xs={12} md={7}>
              <Controller
                name="CommodityTypeName"
                control={control}
                rules={{ required: "Commodity type name is required" }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Name"
                    variant="outlined"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    size="small"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    disabled={isReadOnly}
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Controller
                name="IsActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                        disabled={isReadOnly}
                      />
                    }
                    label="Enabled"
                  />
                )}
              />
            </Grid>
            {isSystemType && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  System commodity types cannot be edited or deleted.
                </Typography>
              </Grid>
            )}
            <Grid item xs={6} md={4} style={{ marginLeft: "auto" }}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => onHide({ shouldUpdate: false, close: true })}
              >
                CLOSE
              </Button>
            </Grid>
            <Grid item xs={6} md={4}>
              <Button
                fullWidth
                startIcon={isSubmitting && <CircularProgress size="1rem" />}
                disabled={isSubmitting || !isFormDirty || isReadOnly}
                variant="contained"
                color="primary"
                type="submit"
              >
                SAVE
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
      <NotificationSnackBar
        message={message}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
